import React, { useEffect, useState } from 'react'
import { isEmpty } from 'lodash'

import Tooltip from '../Tooltip/Tooltip'
import Icon from '../Icon2'
import { openChooseAssetModal } from '../AssetPicker'
import BranchDisplay from '../BranchDisplay/BranchDisplay'

import RuleModuleValueSelect from './RuleModuleValueSelect'
import RulePermissionModuleSelect from './RulePermissionModuleSelect'
import { InternalRule, ModuleOptionItem, RuleBlockProps } from './types'

const RuleBlock: React.FC<RuleBlockProps> = (props) => {
    const {
        vendor,
        rule,
        index,
        uniqueKey,
        ruleBuildFor,
        selectActionOptions,
        selectModuleOptions,
        onPermissionActionChange,
        onModuleChange,
        onDeleteRuleBlock,
        hasBranchFeature,
        openItemsChooseModal,
        fetchItemsData,
        enableLottiePreview,
        assetPickerModalMultiBranchInfo,
        selectedModuleValue,
        setSelectedModuleValues,
    } = props

    const ruleModuleValue = typeof rule.module === 'object' ? rule.module.value : rule.module
    let ruleModule: any = ruleModuleValue === 'folder' ? 'folders' : ruleModuleValue === 'asset' ? 'assets' : '$all'
    if (ruleBuildFor === 'locale') {
        ruleModule = 'locales'
    }
    const isFolder = ruleModuleValue === 'folder'

    const handleActionChange = (newValue) => {
        onPermissionActionChange(index, newValue)
    }

    const handleModuleChange = (newValue) => {
        if (!newValue) {
            onModuleChange(index, selectModuleOptions[0])
        } else {
            onModuleChange(index, newValue)
            setSelectedValue([]) // Reset selectedValue when module changes
        }
    }

    const handleDeleteRuleBlock = () => {
        onDeleteRuleBlock(index)
    }

    const [selectedValue, setSelectedValue] = useState<any>(selectedModuleValue?.[ruleModule] || [])

    useEffect(() => {
        setSelectedValue(selectedModuleValue?.[ruleModule] || [])
    }, [selectedModuleValue, ruleModule])

    const handleAssetModuleValueChange = (type: 'folders' | 'assets' | '$all', values: any[]) => {
        setSelectedModuleValues((prevState) => {
            // Ensure uniqueness based on uid
            const uniqueValues = values ? values.filter((item, index, self) =>
                index === self.findIndex(v => v?.uid === item?.uid)
            ) : []

            // Check if the values have actually changed
            const currentValues = prevState[index]?.[type] || []
            const hasChanged = uniqueValues.length !== currentValues.length ||
                uniqueValues.some((item, idx) => item?.uid !== currentValues[idx]?.uid)

            if (!hasChanged) {
                return prevState // No change, return same reference
            }

            return {
                ...prevState,
                [index]: {
                    // no need to preserve existing properties of this index,
                    // since this could be a module change as well
                    [type]: uniqueValues
                }
            }
        })
    }

    useEffect(() => {
        if (ruleBuildFor === 'asset') {
            handleAssetModuleValueChange(ruleModule, selectedValue)
        }
    }, [selectedValue, ruleModule, ruleBuildFor])

    const handleOpenChooseModal = () => {
        const assetModalArgs = {
            vendor: 'asset_management',
            excludeAssetUids: selectedModuleValue?.[ruleModule] || [],
            enableLottiePreview,
            getAssets: (args) => fetchItemsData(args),
            onSubmit: (values) => setSelectedValue((prev) => [...(Array.isArray(prev) ? prev : []), ...values]),
            modalTitle:
                (typeof rule.module === 'object' && rule.module?.subLabel) || (isFolder ? 'Select Folders' : 'Select Assets'),
            modalApplyButtonText: isFolder ? 'Add Selected Folders' : 'Add Selected Assets',
            isFolderSelection: isFolder,
            multiple: true,
            showCTAIcon: false,
            showUserDataColumn: false,
            hasBranchFeature,
            multiBranchInfo: hasBranchFeature
                ? isFolder
                    ? assetPickerModalMultiBranchInfo?.folders || ''
                    : assetPickerModalMultiBranchInfo?.assets || ''
                : undefined
        }
        if (typeof openItemsChooseModal === 'function') {
            openItemsChooseModal()
        } else {
            switch (ruleBuildFor) {
                case 'asset':
                    openChooseAssetModal(assetModalArgs as any)
                    break
                default:
                    console.log('Error: No valid modal function provided')
            }
        }
    }

    const getRuleModuleValueSelect = () => {
        switch (ruleBuildFor) {
            case 'asset':
                return (
                    <div className={rule.shouldRestrict ? 'RuleBuilder__content--section--body__row-disabled' : ''}>
                        <RuleModuleValueSelect
                            rule={rule}
                            index={index}
                            vendor={vendor}
                            setSelectedModuleValues={setSelectedModuleValues}
                            fetchModal={handleOpenChooseModal}
                            placeHolder={(typeof rule.module === 'object' && rule?.module?.subLabel) || ''}
                            selectedValue={selectedValue}
                            hasBranchFeature={hasBranchFeature}
                            uniqueKey={`${uniqueKey}-${ruleModule}-module-value-select`}
                            errorMessage={rule?.formError?.moduleValueError}
                        />
                    </div>
                )
            case 'locale':
                const generateBranchDisplayOption = (option: ModuleOptionItem) => {
                    if (!hasBranchFeature) {
                        return {
                            value: option?.uid,
                            label: option?.name,
                            ...(option || {})
                        }
                    }
                    // 'branch-display-dom' is UI-React dom element
                    let appendToId = 'branch-display-dom'
                    let branchDisplayDOM = document.getElementById('branch-display-dom')
                    // Temporary element for the venus to render in Storybook
                    // 'venus-branch-display-dom' is Storybook dom element
                    if (!branchDisplayDOM) {
                        appendToId = 'venus-branch-display-dom'
                        branchDisplayDOM = document.createElement('div')
                        branchDisplayDOM.id = appendToId
                        document.body.appendChild(branchDisplayDOM)
                        branchDisplayDOM = document.getElementById(appendToId)
                    }
                    // Function to normalize field to nested array structure for BranchDisplay
                    const normalizeField = (field: any, isBranch = false) => {
                        if (!hasBranchFeature && Array.isArray(field)) return field[0]

                        // hasBranchFeature - true && isBranch - false
                        if (!isBranch) {
                            return !Array.isArray(field) ? [field] : field
                        }
                        return field.length > 0 && !Array.isArray(field[0]) ? field.map((item: string) => [item]) : field
                    }

                    // Workaround for Asset Management V2 to ensure consistent array structure,
                    // to support on BranchDisplay
                    option = {
                        ...option,
                        name: normalizeField(option.name || option.label),
                        ...(option?._branches || option?._workspaces ? { _branches: normalizeField(option?._branches || option?._workspaces || [[]], true) } : {})
                    }

                    return {
                        ...option,
                        value: option?.value || option?.uid,
                        label: (
                            <Tooltip
                                content={<BranchDisplay moduleType={'locale'} moduleVal={option} titleKey={'name'} vendor={vendor} />}
                                position="right"
                                showArrow={false}
                                appendTo={branchDisplayDOM}>
                                <span>
                                    {Array.isArray(option.name) || Array.isArray(option.label)
                                        ? option?.label?.[0] || option?.name?.[0]
                                        : option?.name || option?.label}
                                </span>
                            </Tooltip>
                        ),
                        searchableLabel: option?.name[0] || option?.name,
                    }
                }
                const localeSelectModuleOptions = selectModuleOptions.map((loc) => generateBranchDisplayOption(loc))
                const localeSelectedValues = (selectedModuleValue?.locales || []).map((loc) => generateBranchDisplayOption(loc))

                const handleLocaleModuleValuesChange = (newValues) => {
                    setSelectedModuleValues((prev) => ({
                        ...prev,
                        [index]: {
                            locales: newValues
                        }
                    }))
                }
                return (
                    <RulePermissionModuleSelect
                        index={index}
                        type="module-value"
                        placeholder={'Select Languages'}
                        value={localeSelectedValues}
                        selectOptions={localeSelectModuleOptions}
                        handleChange={handleLocaleModuleValuesChange}
                        isMultiSelect={true}
                        testId={`${uniqueKey}-${ruleModule}-module-value-select`}
                        hasBranchFeature={hasBranchFeature}
                        errorMessage={rule?.formError?.moduleValueError}
                    />
                )
            default:
                return null
        }
    }

    return (
        <div data-test-id={`${uniqueKey}`} className="RuleBuilder__content--section--body" key={uniqueKey}>
            <div className="separator" />
            <div className="RuleBuilder__content--section--body__row">
                <div className={rule.shouldRestrict ? 'RuleBuilder__content--section--body__row-disabled' : ''}>
                    <RulePermissionModuleSelect
                        index={index}
                        type="action"
                        value={(rule as InternalRule).actions}
                        placeholder="Select Permission(s)"
                        selectOptions={selectActionOptions}
                        handleChange={handleActionChange}
                        isMultiSelect={true}
                        testId={`${uniqueKey}-action-select`}
                        errorMessage={rule.formError?.permissionError}
                    />
                </div>
                {ruleBuildFor !== 'locale' ? (
                    <>
                        <div className="separator" />
                        <div className={rule.shouldRestrict ? 'RuleBuilder__content--section--body__row-disabled' : ''}>
                            <RulePermissionModuleSelect
                                index={index}
                                type="module"
                                value={isEmpty(rule.module) ? selectModuleOptions[0] : rule.module}
                                selectOptions={selectModuleOptions}
                                handleChange={handleModuleChange}
                                isMultiSelect={false}
                                testId={`${uniqueKey}-${ruleModule}-module-select`}
                            />
                        </div>
                    </>
                ) : null}
                {ruleModule !== '$all' ? (
                    <>
                        <div className="separator" />
                        {getRuleModuleValueSelect()}
                    </>
                ) : null}
                <div className="separator" />
                <div
                    onClick={handleDeleteRuleBlock}
                    id={`${uniqueKey}-${ruleModule}-module-delete`}
                    className="remove-rule"
                    data-test-id={`${uniqueKey}-${ruleModule}-module-delete`}>
                    <Tooltip content="Delete" position="right" testId={`${uniqueKey}-${ruleModule}-module-delete-tooltip`}>
                        <Icon icon="Delete" version="v2" size="medium" testId={`${uniqueKey}-${ruleModule}-module-delete-btn`} />
                    </Tooltip>
                </div>
            </div>
        </div>
    )
}

export default RuleBlock
